import { useCallback, useEffect, useState } from "react";
import { createContainer } from "unstated-next";
import { latestVersion } from "../screens/forms/tax/calc/taxCalcService";
import { fetchWhiteListedVehicles } from "../services/miscService";
import { WhiteListedVehicle } from "../types/appTypes";
import { initTaxCase, TaxCase } from "../types/taxTypes";
import { convertToUploadFileList, isApiFiles } from "../utilities/convertTypes";
import { isValidNum } from "../utilities/typeGuard";
import UserStore from "./userStore";

interface TaxCaseStoreProps {
  isAdmin: boolean;
  taxCase: TaxCase;
  setTaxCase: (newCase: TaxCase) => void;

  canSendInvoice: boolean;
  setCanSendInvoice: React.Dispatch<React.SetStateAction<boolean>>;

  isWhiteListedVehicle: boolean;
  whiteListedVehicles: WhiteListedVehicle[]
}

const useTaxCaseStore = (initialState?: TaxCase): TaxCaseStoreProps => {
  const [taxCase, setTaxCase] = useState<TaxCase>(initTaxCase);
  const [canSendInvoice, setCanSendInvoice] = useState<boolean>(true);
  const [whiteListedVehicles, setWhiteListedVehicles] = useState<WhiteListedVehicle[]>([])
  const [isWhiteListedVehicle, setIsWhiteListedVehicle] = useState(false)

  const { hasPermission } = UserStore.useContainer();
  const isAdmin = hasPermission("tax.isAdmin");



  useEffect(() => {
    async function doFetch() {
      try {
        const { data } = await fetchWhiteListedVehicles()
        setWhiteListedVehicles(data?.data ?? [])
      } catch (error) {
        console.error(error)
      }
    }
    doFetch()
  }, [])

  useEffect(() => {
    if (whiteListedVehicles?.length && taxCase.vehicle?.motorAtlasId) {
      const inList = whiteListedVehicles?.some((item) => item.motorAtlasId === taxCase.vehicle?.motorAtlasId)
      setIsWhiteListedVehicle(!!inList)
    } else {
      setIsWhiteListedVehicle(false)
    }
  }, [whiteListedVehicles, taxCase.vehicle?.motorAtlasId])

  const handleSetTaxCase = useCallback(
    (newCase: TaxCase) => {
      const files = newCase.files && isApiFiles(newCase.files) ? convertToUploadFileList(newCase.files) : newCase.files;
      const legalFiles =
        newCase.legalFiles && isApiFiles(newCase.legalFiles)
          ? convertToUploadFileList(newCase.legalFiles)
          : newCase.legalFiles;

      if (!newCase.version) {
        newCase.version = latestVersion;
      }

      setTaxCase({
        ...newCase,
        files,
        legalFiles,
      });
    },
    [setTaxCase]
  );

  useEffect(() => {
    if (initialState) {
      handleSetTaxCase(initialState);
      setCanSendInvoice(isValidNum(initialState?.scenarios?.optimization?.calculation?.offer?.totalPrice));
    }
  }, [initialState, handleSetTaxCase, setCanSendInvoice]);

  return {
    isAdmin,
    taxCase,
    setTaxCase: handleSetTaxCase,
    canSendInvoice,
    setCanSendInvoice,
    isWhiteListedVehicle,
    whiteListedVehicles
  };
};

const TaxCaseStore = createContainer(useTaxCaseStore);

export default TaxCaseStore;
